import { CategoryInfo } from 'pages/insights-pro/category-tab/model';
import { MetricsChart } from 'components/MetricsChart';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { useIntl } from 'react-intl';
import { formatDateRangeLabel } from 'pages/insights-pro/country-tab/util';
import { getArrayWithIsOthers } from 'pages/insights-pro/common/graphs/getArrayWithIsOthers';
import { TileContent } from 'components/Layout/PageContainer';
import { MeasureUtil, useModelService } from 'services/model/model.service';
import { DataResult } from 'models/data';
import { useAppStore } from 'hooks/state/useAppStore';

interface CategoriesReturnRatesProps {
  total: DataResult;
  categories: CategoryInfo[];
  isComparisonActive: boolean;
}

export function CategoriesReturnRates({ total, categories, isComparisonActive }: CategoriesReturnRatesProps) {
  const { getMeasures } = useModelService();

  const intl = useIntl();
  const { dateRange } = useAppStore();

  const [retQtyRate, retQty, slsQty] = getMeasures(['retQtyRateExpOD', 'retQtyExpOD', 'slsQtyOD']);

  const groupedCategories = getArrayWithIsOthers(categories, [retQtyRate.code, slsQty.code, retQty.code], intl, c => ({
    ...c,
    entry: {
      ...c.entry,
      values: {
        [retQtyRate.code]: MeasureUtil.divideOrZero(retQty, slsQty, c.entry.values),
      },
      compareValues: {
        [retQtyRate.code]: MeasureUtil.divideOrZero(retQty, slsQty, c.entry.compareValues),
      },
    },
  })).sort((a, b) => retQtyRate.getValue(b.entry.values) - retQtyRate.getValue(a.entry.values));

  const createReturnRateChartConfig = () => {
    const returnRateChartConfig = BarChartConfig.forTBMeasure(retQtyRate);

    returnRateChartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,
        formatDateRangeLabel(dateRange.range, intl),
        [...groupedCategories].reverse().map(category => {
          const data = new ChartData(category.name, retQtyRate.getValue(category.entry.values));
          data.color = category.color;
          return data;
        })
      ),
    ];
    if (isComparisonActive) {
      returnRateChartConfig.data.push(
        new ChartDataArray(
          ChartDataType.primary,

          formatDateRangeLabel(dateRange.comparison, intl),
          [...groupedCategories]
            .reverse()
            .map(category => new ChartData(category.name, retQtyRate.getValue(category.entry.compareValues))),
          ChartDataComparisonType.comparison
        )
      );
    }
    returnRateChartConfig.data.push(
      new ChartDataArray(
        ChartDataType.average,
        intl.formatMessage({ id: 'general.label.total' }),
        [retQtyRate.getValue(total.values)].map(avg => new ChartData('AVG', avg))
      )
    );

    return returnRateChartConfig;
  };

  const returnRateChartRenderer = new BarChartRenderer(createReturnRateChartConfig());

  return (
    <TileContent className="return-rates">
      <MetricsChart renderer={returnRateChartRenderer} height="h-[400px]" />
    </TileContent>
  );
}
